'use strict';

import React, { useState, useContext, useEffect } from 'react';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Réservations reçues',
    sousTitre: 'Toutes les réservations sur vos terres, payées ou en attente.',
    retour: '← Mon espace',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger vos réservations.',
    aucune: "Aucune réservation pour l'instant.",
    colTerritoire: 'Territoire',
    colChasseur: 'Chasseur',
    colMontant: 'Montant',
    colDate: 'Date',
    colPaiement: 'Paiement',
    paiementEnAttente: 'En attente',
    paiementSucceeded: 'Payé',
    paiementFailed: 'Échec',
    paiementRefunded: 'Remboursé',
  },
  en: {
    title: 'Reservations received',
    sousTitre: 'All bookings on your lands, paid or pending.',
    retour: '← My dashboard',
    chargement: 'Loading…',
    erreur: 'Could not load your reservations.',
    aucune: 'No reservations yet.',
    colTerritoire: 'Territory',
    colChasseur: 'Hunter',
    colMontant: 'Amount',
    colDate: 'Date',
    colPaiement: 'Payment',
    paiementEnAttente: 'Pending',
    paiementSucceeded: 'Paid',
    paiementFailed: 'Failed',
    paiementRefunded: 'Refunded',
  },
};

const OR = 'var(--couleur-or)';

function formatMontant(value, lang) {
  if (value == null) return '—';
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

export default function MyReservations() {
  const { lang } = useContext(LangContext);
  const t = T[lang];
  const [reservations, setReservations] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    api
      .get('/proprietaire/reservations')
      .then((r) => {
        if (!annule) {
          setReservations(r.data);
          setErreur(false);
        }
      })
      .catch(() => {
        if (!annule) setErreur(true);
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  const paymentStyles = {
    en_attente: {
      bg: 'rgba(201,162,39,0.12)',
      bd: 'rgba(201,162,39,0.3)',
      fg: 'var(--couleur-or)',
      lbl: t.paiementEnAttente,
    },
    succeeded: {
      bg: 'rgba(40,160,80,0.15)',
      bd: 'rgba(40,160,80,0.4)',
      fg: 'var(--couleur-texte-succes)',
      lbl: t.paiementSucceeded,
    },
    failed: {
      bg: 'rgba(200,50,50,0.12)',
      bd: 'rgba(200,50,50,0.4)',
      fg: 'var(--couleur-texte-erreur)',
      lbl: t.paiementFailed,
    },
    refunded: {
      bg: 'rgba(120,120,200,0.12)',
      bd: 'rgba(120,120,200,0.4)',
      fg: '#a0a0e8',
      lbl: t.paiementRefunded,
    },
  };

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="📅" title={t.title} subtitle={t.sousTitre} />

        {chargement && (
          <div
            style={{
              color: 'rgba(255,255,255,0.35)',
              fontSize: '0.85rem',
              padding: '1.2rem',
              textAlign: 'center',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
            }}
          >
            {t.chargement}
          </div>
        )}

        {!chargement && erreur && (
          <div
            style={{
              background: 'rgba(200,50,50,0.1)',
              border: '1px solid rgba(200,50,50,0.35)',
              borderRadius: '8px',
              padding: '0.8rem 1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.85rem',
            }}
          >
            ⚠ {t.erreur}
          </div>
        )}

        {!chargement && !erreur && reservations && reservations.length === 0 && (
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(184,134,11,0.4)',
              borderRadius: '8px',
              padding: '1.4rem 1rem',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.88rem',
            }}
          >
            {t.aucune}
          </div>
        )}

        {!chargement && !erreur && reservations && reservations.length > 0 && (
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 0.8fr 0.7fr 0.65fr 0.7fr',
                padding: '0.6rem 1rem',
                background: 'rgba(255,255,255,0.04)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                fontSize: '0.65rem',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              <span>{t.colTerritoire}</span>
              <span>{t.colChasseur}</span>
              <span>{t.colMontant}</span>
              <span>{t.colDate}</span>
              <span>{t.colPaiement}</span>
            </div>
            {reservations.map((r, i) => {
              const territoire = r.publication?.terre?.title || '—';
              const date = r.createdAt
                ? new Date(r.createdAt).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')
                : '—';
              const st = paymentStyles[r.payment_status] || paymentStyles.en_attente;
              return (
                <div
                  key={r.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 0.8fr 0.7fr 0.65fr 0.7fr',
                    padding: '0.75rem 1rem',
                    borderBottom:
                      i < reservations.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    fontSize: '0.82rem',
                    alignItems: 'center',
                    background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--couleur-texte)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {territoire}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
                    {r.chasseur_anonymise}
                  </span>
                  <span style={{ color: OR, fontWeight: 700 }}>
                    {formatMontant(r.amount, lang)}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                    {date}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: '0.65rem',
                      background: st.bg,
                      border: `1px solid ${st.bd}`,
                      color: st.fg,
                      borderRadius: '20px',
                      padding: '2px 8px',
                      width: 'fit-content',
                    }}
                  >
                    {st.lbl}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PrivateLayout>
  );
}
